import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';

// Badge colors per category
const BADGES = {
  patches: { light: 'bg-amber-100 text-amber-800 ring-amber-200', solid: 'bg-amber-500 text-white', bar: 'from-amber-500 to-orange-400' },
  vectors: { light: 'bg-sky-100 text-sky-800 ring-sky-200', solid: 'bg-sky-500 text-white', bar: 'from-sky-500 to-cyan-400' },
  digitizing: { light: 'bg-emerald-100 text-emerald-800 ring-emerald-200', solid: 'bg-emerald-500 text-white', bar: 'from-emerald-500 to-teal-400' },
};

const cap = (s) => s.charAt(0).toUpperCase() + s.slice(1);

const PortfolioShowcase = () => {
  const [activeCategory, setActiveCategory] = useState('all');
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // All images from Home folder organized by category
  const portfolioItems = [
    // Patches Category
    { id: 1, category: 'patches', title: 'Custom Embroidered Patches Collection', image: '/Home/before8.jpg', type: 'single' },
    { id: 2, category: 'patches', title: 'Custom Hat Patches & Emblems', image: '/Home/before14.jpg', type: 'single' },
    { id: 3, category: 'patches', title: 'Professional Patch Design', image: '/portfolio-snapshot/patches/p3.jpg', type: 'single' },
    { id: 4, category: 'patches', title: 'Custom patches', image: '/portfolio-snapshot/patches/p4.jpg', type: 'single' },

    // Vectors Category
    { id: 5, category: 'vectors', title: 'Vector Art Transformation', image: '/Home/before9.jpg', type: 'single' },
    { id: 6, category: 'vectors', title: 'Flag Vector Design', image: '/Home/before10.jpg', type: 'single' },
    { id: 7, category: 'vectors', title: 'Custom Vector Design', image: '/Home/vector.jpg', type: 'single' },
    { id: 8, category: 'vectors', title: 'Custom Military & Sports Vector', image: '/Home/before7.jpg', type: 'single' },

    // Digitizing Category
    { id: 10, category: 'digitizing', title: 'Professional Embroidery Digitizing', image: '/Home/before11.jpg', type: 'single' },
    { id: 11, category: 'digitizing', title: 'Custom Logo Digitizing Collection', image: '/Home/d3a.jpeg', type: 'single' },
    { id: 12, category: 'digitizing', title: 'Professional Digitizing Services', image: '/Home/before17.jpg', type: 'single' },
    { id: 13, category: 'digitizing', title: 'Custom Cap Embroidery', image: '/Home/cap.jpg', type: 'single' },
  ];

  const categories = [
    { id: 'all', label: 'All Work', count: portfolioItems.length },
    { id: 'patches', label: 'Patches', count: portfolioItems.filter(item => item.category === 'patches').length },
    { id: 'vectors', label: 'Vectors', count: portfolioItems.filter(item => item.category === 'vectors').length },
    { id: 'digitizing', label: 'Digitizing', count: portfolioItems.filter(item => item.category === 'digitizing').length }
  ];

  const filteredItems = activeCategory === 'all'
    ? portfolioItems
    : portfolioItems.filter(item => item.category === activeCategory);

  const openLightbox = (item) => {
    setSelectedImage(item);
    setLightboxOpen(true);
  };

  const closeLightbox = () => {
    setLightboxOpen(false);
    setSelectedImage(null);
  };

  // Close lightbox with Escape
  useEffect(() => {
    if (!lightboxOpen) return;
    const onKey = (e) => e.key === 'Escape' && closeLightbox();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [lightboxOpen]);

  return (
    <>
      <section className="relative overflow-hidden bg-slate-950 py-24">
        {/* Background glow + dots */}
        <div className="pointer-events-none absolute -left-24 top-0 h-96 w-96 rounded-full bg-orange-600/40 blur-3xl" />
        <div className="pointer-events-none absolute -right-24 top-1/3 h-96 w-96 rounded-full bg-red-500/30 blur-3xl" />
        <div className="pointer-events-none absolute bottom-0 left-1/3 h-96 w-96 rounded-full bg-amber-500/20 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.07]" style={{ backgroundImage: 'radial-gradient(#fff 1px, transparent 1px)', backgroundSize: '24px 24px' }} />

        <div className="container relative mx-auto px-6">

          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="mb-14 text-center"
          >
            <h2 className="mb-6 text-4xl font-black leading-tight tracking-tight text-white md:text-6xl">
              Our Professional{" "}
              <span className="bg-gradient-to-r from-orange-500 via-red-500 to-amber-500 bg-clip-text text-transparent">
                Design Portfolio
              </span>
            </h2>

            <p className="mx-auto max-w-3xl text-xl leading-relaxed text-slate-300">
              Embroidery Digitizing, Vector Art Work & Embroidery Patches -
              Showcasing our expertise through real client transformations
            </p>
          </motion.div>

          {/* Category Filter */}
          <div className="mx-auto mb-14 flex max-w-fit flex-wrap justify-center gap-2 rounded-3xl bg-white/5 p-2 ring-1 ring-white/10 backdrop-blur">
            {categories.map((category) => (
              <button
                key={category.id}
                onClick={() => setActiveCategory(category.id)}
                className={`rounded-2xl px-6 py-3 font-semibold transition-all duration-300 ${
                  activeCategory === category.id
                    ? 'bg-gradient-to-r from-orange-500 via-red-500 to-amber-500 text-white shadow-lg shadow-orange-500/30'
                    : 'text-slate-300 hover:bg-white/10 hover:text-white'
                }`}
              >
                {category.label} ({category.count})
              </button>
            ))}
          </div>

          {/* Portfolio Grid - 2 columns on medium and large screens */}
          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-2">
            {filteredItems.map((item, index) => {
              const b = BADGES[item.category];
              return (
                <motion.div
                  key={item.id}
                  initial={{ opacity: 0, y: 50 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: index * 0.1 }}
                  className="group overflow-hidden rounded-3xl bg-white shadow-2xl shadow-black/30 ring-1 ring-white/10 transition duration-300 hover:-translate-y-1 hover:shadow-orange-500/20"
                >
                  <div className={`h-1.5 w-full bg-gradient-to-r ${b.bar}`} />
                  <div className="p-6">
                    <h3 className="mb-4 text-center text-lg font-bold leading-tight text-slate-900">
                      {item.title}
                    </h3>

                    <div className="relative cursor-pointer overflow-hidden rounded-2xl bg-slate-100 ring-1 ring-slate-200">
                      <img
                        src={item.image}
                        alt={item.title}
                        className="h-72 w-full object-cover transition-transform duration-500 group-hover:scale-105"
                        onError={(e) => {
                          console.log('Image failed to load:', item.image);
                          e.target.style.display = 'none';
                        }}
                        onClick={() => openLightbox(item)}
                      />
                      <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-slate-900/40 via-transparent to-transparent opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
                      <span className="pointer-events-none absolute bottom-3 right-3 flex h-10 w-10 items-center justify-center rounded-full bg-white/90 text-slate-900 opacity-0 shadow-lg transition-opacity duration-300 group-hover:opacity-100">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v6m3-3H7" />
                        </svg>
                      </span>
                    </div>

                    <div className="mt-4 text-center">
                      <span className={`rounded-full px-4 py-2 text-sm font-semibold ring-1 ${b.light}`}>
                        {cap(item.category)}
                      </span>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* CTA Button */}
          <div className="mt-16 text-center">
            <Link to="/portfolio">
              <button className="transform rounded-full bg-gradient-to-r from-orange-500 via-red-500 to-amber-500 px-10 py-4 font-bold text-white shadow-xl shadow-orange-500/30 ring-4 ring-white/10 transition-all duration-300 hover:scale-105 hover:shadow-2xl hover:shadow-orange-500/50">
                View Full Portfolio
              </button>
            </Link>
          </div>

        </div>
      </section>

      {/* Lightbox Modal */}
      <AnimatePresence>
        {lightboxOpen && selectedImage && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.3 }}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/95 p-4 backdrop-blur-md"
            onClick={closeLightbox}
          >
            {/* Close button */}
            <button
              onClick={closeLightbox}
              className="absolute right-4 top-4 z-10 flex h-12 w-12 items-center justify-center rounded-full bg-white/10 text-4xl font-bold text-white ring-1 ring-white/20 transition hover:bg-white/20"
            >
              ×
            </button>

            {/* Image container */}
            <motion.div
              initial={{ scale: 0.92, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3 }}
              className="relative flex max-h-[95vh] max-w-[95vw] items-center justify-center"
            >
              <img
                src={selectedImage.image}
                alt={selectedImage.title}
                className="max-h-full max-w-full rounded-2xl object-contain shadow-2xl shadow-orange-500/20 ring-1 ring-white/10"
                style={{ maxWidth: '95vw', maxHeight: '95vh', width: 'auto', height: 'auto' }}
                onClick={(e) => e.stopPropagation()}
              />

              {/* Image title overlay */}
              <div className="absolute bottom-0 left-0 right-0 rounded-b-2xl bg-gradient-to-t from-slate-950/95 to-slate-950/60 p-4 text-white backdrop-blur-sm">
                <h3 className="mb-2 text-center text-lg font-semibold">{selectedImage.title}</h3>
                <div className="text-center">
                  <span className={`inline-block rounded-full px-3 py-1 text-xs font-medium ${BADGES[selectedImage.category].solid}`}>
                    {cap(selectedImage.category)}
                  </span>
                </div>
              </div>
            </motion.div>

            {/* Instructions */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 transform text-sm text-white/70">
              Click outside to close
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default PortfolioShowcase;